import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { components } from "../api/schema";
import { Card, EmptyState, Kpi } from "./basics";
import { DataTable } from "./DataTable";
import { TEST_RESULTS, TestResultChip, type TestResult } from "./TestResultChip";

export type QaProgressData = components["schemas"]["QaProgressOut"];
export type QaStory = components["schemas"]["QaStoryOut"];

/* The QA progress report (docs/test-suite.md section 14), shared by the staff Progress tab and the
   portal Testing tab: how many tests pass, what is open, failing or blocked, which stories still
   have open tests, how each environment is doing and 14 days of runs. Plain SVG, no chart library;
   colours are the badge colours so the report follows the theme. */

const percent = (rate: number) => `${Math.round(rate * 100)}%`;
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDay = (iso: string) => `${MON[+iso.slice(5, 7) - 1]} ${+iso.slice(8, 10)}`;

/** The order of the stacked bar: good to bad, then the rest. */
const BAR: TestResult[] = [
  "pass",
  "retest",
  "not_run",
  "manual",
  "waiting",
  "untestable",
  "fail",
  "blocked",
  "functionality_changed",
  "waived",
  "na",
];
const BAR_COLOUR: Record<TestResult, string> = {
  pass: "var(--ok)",
  fail: "var(--bad)",
  blocked: "var(--warn)",
  retest: "var(--info)",
  not_run: "var(--line)",
  manual: "var(--purple)",
  waiting: "var(--fg-3)",
  untestable: "var(--fg-3)",
  functionality_changed: "var(--warn)",
  waived: "var(--line)",
  na: "var(--line)",
};

function ResultsBar({ data }: { data: QaProgressData }) {
  const parts = BAR.filter((r) => (data.counts[r] ?? 0) > 0);
  const summary = parts.map((r) => `${data.counts[r]} ${TEST_RESULTS[r].label}`).join(", ");
  return (
    <div>
      <div
        role="img"
        aria-label={`Results of ${data.tests} tests: ${summary}`}
        style={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden" }}
      >
        {parts.map((r) => (
          <span
            key={r}
            title={`${TEST_RESULTS[r].label}: ${data.counts[r]}`}
            style={{ flex: data.counts[r], background: BAR_COLOUR[r] }}
          />
        ))}
      </div>
      <ul className="burn-legend" aria-label="Results">
        {parts.map((r) => (
          <li key={r}>
            <TestResultChip result={r} prefix={<b>{data.counts[r]} </b>} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Runs per day, as stacked bars: passed, failed, blocked and other. */
function RunsChart({ days }: { days: QaProgressData["runs_by_day"] }) {
  const total = (d: (typeof days)[number]) => d.passed + d.failed + d.blocked + d.other;
  const max = Math.max(1, ...days.map(total));
  const W = 560;
  const H = 120;
  const slot = W / Math.max(days.length, 1);
  const bar = slot * 0.64;
  const sum = days.reduce((n, d) => n + total(d), 0);
  const stacks: [keyof (typeof days)[number], string][] = [
    ["passed", "var(--ok)"],
    ["failed", "var(--bad)"],
    ["blocked", "var(--warn)"],
    ["other", "var(--fg-3)"],
  ];
  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H + 18}`}
        width="100%"
        role="img"
        aria-label={`Test runs in the last ${days.length} days: ${sum} in total. The table below has each day.`}
        style={{ maxHeight: 180 }}
      >
        <line x1={0} x2={W} y1={H} y2={H} stroke="var(--line)" />
        {days.map((d, i) => {
          let y = H;
          const x = i * slot + (slot - bar) / 2;
          return (
            <g key={d.day}>
              <title>{`${shortDay(d.day)}: ${total(d)} runs (${d.passed} passed, ${d.failed} failed, ${d.blocked} blocked, ${d.other} other)`}</title>
              {stacks.map(([k, fill]) => {
                const n = d[k] as number;
                if (!n) return null;
                const h = (n / max) * (H - 6);
                y -= h;
                return <rect key={k} x={x} y={y} width={bar} height={h} fill={fill} />;
              })}
              {(i === 0 || i === days.length - 1 || i % 4 === 0) && (
                <text x={x + bar / 2} y={H + 13} textAnchor="middle" fontSize={10} fill="var(--fg-3)">
                  {shortDay(d.day)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <ul className="burn-legend" aria-label="Run results">
        {(
          [
            ["Passed", "var(--ok)"],
            ["Failed", "var(--bad)"],
            ["Blocked", "var(--warn)"],
            ["Other", "var(--fg-3)"],
          ] as const
        ).map(([label, colour]) => (
          <li key={label}>
            <span aria-hidden style={{ color: colour }}>
              ■{" "}
            </span>
            {label}
          </li>
        ))}
      </ul>
      <table className="sr-only">
        <caption>Test runs per day</caption>
        <thead>
          <tr>
            <th>Day</th>
            <th>Passed</th>
            <th>Failed</th>
            <th>Blocked</th>
            <th>Other</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.day}>
              <td>{shortDay(d.day)}</td>
              <td>{d.passed}</td>
              <td>{d.failed}</td>
              <td>{d.blocked}</td>
              <td>{d.other}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function QaProgress({
  data,
  staff = false,
  storyHref,
  action,
}: {
  data: QaProgressData;
  /** Staff are told tests are added through the MCP when there are none. */
  staff?: boolean;
  /** Where a story links to (return null for plain text); the portal has no story page. */
  storyHref?: (story: QaStory) => string | null;
  /** A link beside the heading, e.g. "Open Testing". */
  action?: ReactNode;
}) {
  const noTests = data.environments.length === 0;
  if (noTests && data.functionality_changed_cases === 0)
    return (
      <Card title="QA" action={action}>
        <EmptyState>
          <p>No tests yet.</p>
          {staff && (
            <p className="muted">
              Testing environments and test cases are added through the MCP (ask Claude to write the tests for
              this project's stories).
            </p>
          )}
        </EmptyState>
      </Card>
    );

  const blocked = data.blocked;
  return (
    <div className="stack" aria-label="QA progress">
      <Card title="QA" action={action}>
        <div className="stack">
          <div className="kpis">
            <Kpi
              label="Pass rate"
              value={percent(data.pass_rate)}
              sub={`${data.passed} of ${data.tests - (data.counts.waived ?? 0) - (data.counts.na ?? 0)} tests pass`}
            />
            <Kpi
              label="Open tests"
              value={data.open}
              sub={`in ${data.open_stories + data.other_stories_open} ${data.open_stories + data.other_stories_open === 1 ? "story" : "stories"}`}
            />
            <Kpi label="Failing" value={data.failing} alert={data.failing > 0} />
            <Kpi
              label="Blocked"
              value={blocked}
              alert={blocked > 0}
              sub={blocked > 0 ? `${data.blocked_with_bug} with a bug` : undefined}
            />
            <Kpi label="Retest" value={data.retest} />
            <Kpi label="Not run" value={data.not_run} />
            <Kpi
              label="Functionality changed"
              value={data.functionality_changed_cases}
              sub="tests to rewrite"
            />
          </div>
          {data.tests > 0 && <ResultsBar data={data} />}
          <p className="muted">
            {data.open_bugs_from_tests === 1
              ? "1 open bug from tests."
              : `${data.open_bugs_from_tests} open bugs from tests.`}{" "}
            {data.last_run_at ? "" : "Nothing has been run yet. "}
            {data.open_sessions > 0 &&
              `${data.open_sessions} ${data.open_sessions === 1 ? "session" : "sessions"} in progress.`}
          </p>
        </div>
      </Card>
      <Card title="Environments">
        <DataTable
          label="Environments"
          rows={data.environments}
          rowKey={(e) => e.id}
          empty="No environments."
          columns={[
            {
              header: "Environment",
              cell: (e) => (
                <>
                  <span className="mono">{e.code}</span> {e.name}
                </>
              ),
            },
            { header: "Device", cell: (e) => e.device },
            {
              header: "Results",
              cell: (e) => (
                <span className="chips">
                  {(Object.keys(TEST_RESULTS) as TestResult[])
                    .filter((r) => (e.counts[r] ?? 0) > 0)
                    .map((r) => (
                      <TestResultChip key={r} result={r} prefix={<b>{e.counts[r]} </b>} />
                    ))}
                </span>
              ),
            },
            { header: "Pass rate", align: "right", cell: (e) => percent(e.pass_rate) },
          ]}
        />
      </Card>
      <Card title="Open stories">
        <DataTable
          label="Stories with open tests"
          rows={data.stories}
          rowKey={(s) => s.id}
          empty="No stories with open tests."
          columns={[
            {
              header: "Story",
              cell: (s) => {
                const href = storyHref?.(s) ?? null;
                const key = href ? (
                  <Link className="mono" to={href}>
                    {s.key}
                  </Link>
                ) : (
                  <span className="mono">{s.key}</span>
                );
                return (
                  <>
                    {key} {s.title}
                  </>
                );
              },
            },
            { header: "Open", align: "right", cell: (s) => s.open },
            { header: "Failed", align: "right", cell: (s) => s.failed },
            { header: "Blocked", align: "right", cell: (s) => s.blocked },
          ]}
        />
        {data.more_stories > 0 && (
          <p className="muted">And {data.more_stories} more stories with open tests.</p>
        )}
        {data.other_stories_open > 0 && (
          <p className="muted">
            and {data.other_stories_open} other {data.other_stories_open === 1 ? "story" : "stories"}
          </p>
        )}
      </Card>
      <Card title="Test runs, last 14 days">
        <RunsChart days={data.runs_by_day} />
      </Card>
    </div>
  );
}
